/**
 * Aircraft-on-watch in 10-minute windows, derived from a day's tracked-aircraft
 * list.
 *
 * Moved out of History.js `calculateWindowStats`, with the max-distance series
 * REMOVED rather than carried over. That series was fed from
 * `getMaxDistancePoints`, which returns the daily range-outline table — up to
 * 360 rows keyed by bearing, each timestamped when that bearing set its daily
 * best. Bucketing those into 10-minute windows does not give "furthest contact
 * in this window"; it gives "did some bearing set a record in this window", and
 * windows with no record event were zero-filled, so the line collapsed to the
 * baseline through the afternoon regardless of real range.
 *
 * The genuine interval metric is `max_range_nm` in receiver_perf_stats — a
 * per-minute maximum, reset every minute on the receiver. Section C plots that
 * in its own aligned panel (C7) instead.
 */

const WINDOW_MS = 10 * 60 * 1000;

export function windowRows(aircraftList) {
  if (!aircraftList || aircraftList.length === 0) return [];

  let minTime = null;
  let maxTime = null;
  for (const a of aircraftList) {
    if (a.firstSeen) {
      const t = new Date(a.firstSeen).getTime();
      if (minTime === null || t < minTime) minTime = t;
    }
    if (a.lastSeen) {
      const t = new Date(a.lastSeen).getTime();
      if (maxTime === null || t > maxTime) maxTime = t;
    }
  }
  if (minTime === null || maxTime === null) return [];

  const start = Math.floor(minTime / WINDOW_MS) * WINDOW_MS;
  const end = Math.ceil(maxTime / WINDOW_MS) * WINDOW_MS;

  const arrivals = new Map();
  const departures = new Map();
  for (const a of aircraftList) {
    if (a.firstSeen) {
      const w = Math.floor(new Date(a.firstSeen).getTime() / WINDOW_MS) * WINDOW_MS;
      arrivals.set(w, (arrivals.get(w) || 0) + 1);
    }
    if (a.lastSeen) {
      const w = Math.floor(new Date(a.lastSeen).getTime() / WINDOW_MS) * WINDOW_MS;
      departures.set(w, (departures.get(w) || 0) + 1);
    }
  }

  const rows = [];
  let concurrent = 0;
  for (let w = start; w <= end; w += WINDOW_MS) {
    const low = concurrent;                       // before this window's arrivals
    concurrent += arrivals.get(w) || 0;
    const high = concurrent;                      // after arrivals, before departures
    concurrent -= departures.get(w) || 0;

    rows.push({
      label: new Date(w).toLocaleTimeString([], {
        hour: '2-digit', minute: '2-digit', hour12: false,
      }),
      // Stacked pair: the floor for the window, then the peak above it. Both
      // are aircraft counts, so one scale is honest.
      values: [low, Math.max(0, high - low)],
    });
  }
  return rows;
}

/** Bucket 1-minute max_range samples into the same 10-minute windows, by max. */
export function rangeWindowRows(samples) {
  if (!samples || samples.length === 0) return [];
  const byWindow = new Map();
  for (const p of samples) {
    if (!p || p.v == null) continue;
    const w = Math.floor(p.ts / WINDOW_MS) * WINDOW_MS;
    // MAX, not average: these are already per-minute maxima, and the question
    // this chart answers is "how far did it reach", not "how far on average".
    const prev = byWindow.get(w);
    if (prev == null || p.v > prev) byWindow.set(w, p.v);
  }
  return [...byWindow.keys()].sort((a, b) => a - b).map(w => ({
    ts: w,
    v: byWindow.get(w),
  }));
}
